import * as cookie from 'cookie';
import uniq from 'lodash-es/uniq';
import {
    ACTION_TO_WISHLIST_STROLLER,
    STROLLER_STROLLER_WAS_SAVED_A,
    STROLLER_STROLLER_WAS_SAVED_B,
} from 'mk/autogenerated/translations/WishList.sagas.0f7ead3103d8405b2613d6d38ad8c96a'
import { isAnonymous } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import {
    strollers_api_get_wishlist_ids_url,
    strollers_api_get_wishlist_url,
    strollers_api_set_wishlist_url,
} from 'mk/urls/functions';
import {
    STROLLERS_WISHLIST_IDS_LOAD,
    STROLLERS_WISHLIST_ITEMS_FETCH_TRIGGER,
    STROLLERS_WISHLIST_TOGGLE_TRIGGER,
    strollersWishlistIdsFetch,
    StrollersWishlistIdsFetchNormalizedResponse,
    StrollersWishlistIdsLoadAction,
    strollersWishlistItemsFetchApi,
    StrollersWishlistItemsFetchNormalizedResponse,
    StrollersWishlistItemsFetchTriggerAction,
    StrollersWishlistToggleAction,
    strollersWishlistToggleApi,
} from 'mk2/apps/strollers/containers/WishList/WishList.actions';
import { getStrollersWishListState } from 'mk2/apps/strollers/containers/WishList/WishList.selectors';
import { StrollerSchema } from 'mk2/apps/strollers/schemas';
import { strollersWishlistUrl } from 'mk2/apps/strollers/urls';
import Link from 'mk2/components/Link';
import { handleXHRPostErrorSaga, normalizeError, XHRAction, XHRPostError } from 'mk2/helpers/api';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { getLogger } from 'mk2/logger';
import { getRequestUser } from 'mk2/reducers';
import { checkIfUserIsLoggedIn } from 'mk2/sagas/auth';
import { UserEntity } from 'mk2/schemas';
import { normalize } from 'normalizr';
import React from 'react';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('strollers/WishList.sagas');
const cookieName = 'strollersWishlist';

function* fetchWishlistIds({ xhr }: StrollersWishlistIdsLoadAction & XHRAction) {
    yield put(strollersWishlistIdsFetch.request());
    const requestUser: UserEntity = yield select(getRequestUser);
    // we use infromation from cookie for anonymous user, for signed user we use database information
    if (isAnonymous(requestUser)) {
        try {
            const cookies = (typeof document !== 'undefined') ? cookie.parse(document.cookie) : cookie.parse('');
            const wishlistArray = (cookies.hasOwnProperty(cookieName)) ? uniq(cookies.strollersWishlist.split(',')).filter(Boolean) : [];
            const normalizedResponse: StrollersWishlistIdsFetchNormalizedResponse = normalize({wishlistIds: wishlistArray}, {});
            yield put(strollersWishlistIdsFetch.success(normalizedResponse));
        } catch (error) {
            logger.error(error);
            yield put(strollersWishlistIdsFetch.failure(normalizeError(error)));
        }
    } else {
        try {
        const response = yield call(
            () => xhr.get(strollers_api_get_wishlist_ids_url(requestUser.id)),
        );
        const normalizedResponse: StrollersWishlistIdsFetchNormalizedResponse = normalize(response.body, {});
        yield put(strollersWishlistIdsFetch.success(normalizedResponse));
        } catch (error) {
            yield handleXHRPostErrorSaga(error as XHRPostError, logger);
            yield put(strollersWishlistIdsFetch.failure(normalizeError(error)));
        }
    }
}

function* fetchWishlistItems({ after, xhr }: StrollersWishlistItemsFetchTriggerAction & XHRAction) {
    yield put(strollersWishlistItemsFetchApi.request(after));
    const requestUser = yield select(getRequestUser);
    let ids;

    try {
        // we use infromation from cookie for anonymous user, for signed user we use database information
        if (isAnonymous(requestUser)) {
            const cookies = (typeof document !== 'undefined') ? cookie.parse(document.cookie) : cookie.parse('');
            const idsArray = (cookies.hasOwnProperty(cookieName)) ? uniq(cookies.strollersWishlist.split(',')).filter(Boolean) : [];
            ids = encodeURIComponent(JSON.stringify(idsArray));
        } else {
            const wishlist = yield select(getStrollersWishListState);
            ids = encodeURIComponent(JSON.stringify(wishlist.currentWishlist));

        }
        const response = yield call(
                () => xhr.get(strollers_api_get_wishlist_url({after, ids})),
            );
        const normalizedResponse: StrollersWishlistItemsFetchNormalizedResponse = normalize(response.body, {
            strollers: [StrollerSchema],
        });
        yield put(strollersWishlistItemsFetchApi.success(after, response.body.hasMore, normalizedResponse));
    } catch (error) {
        logger.error(error);
        yield put(strollersWishlistItemsFetchApi.failure(after, normalizeError(error)));
    }
}

interface ApiResponseToggleWishlist {
    body: {
        wishlisted: boolean;
    };
}

function* toggleWishlist({ id, xhr }: StrollersWishlistToggleAction & XHRAction) {
    // for en-version save data to cookie
    if (ACTIVE_SERVER_ID === 207) {
        try {
            let cookieValue = '';
            const cookies = (typeof document !== 'undefined') ? cookie.parse(document.cookie) : cookie.parse('');

            const idstr = id.toString();

            let action = '';
            if (cookies.hasOwnProperty(cookieName)) {
                let wishlistArray = uniq(cookies.strollersWishlist.split(',')).filter(Boolean);
                if (wishlistArray.indexOf(idstr) > -1) {
                    action = 'removed';
                    wishlistArray.splice(wishlistArray.indexOf(idstr), 1);
                } else {
                    action = 'added';
                    if (wishlistArray.length === 0) {
                        wishlistArray = [idstr];
                    } else {
                        wishlistArray.push(idstr);
                    }
                }
                cookieValue = wishlistArray.join(',');
            } else {
                action = 'added';
                cookieValue = idstr;
            }

            cookieValue = cookie.serialize(cookieName, cookieValue, {path: '/'});
            if (typeof document !== 'undefined') {
                document.cookie = cookieValue;
            }

            if (action === 'added') {
                yield put(strollersWishlistToggleApi.success(id, {}));
                yield showSuccessToast(
                    <>
                        {STROLLER_STROLLER_WAS_SAVED_A} <Link to={strollersWishlistUrl}>{STROLLER_STROLLER_WAS_SAVED_B}</Link>.
                    </>,
                );
            }

        } catch (error) {
            logger.error(error);
        }
    } else {
        if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_WISHLIST_STROLLER))) {
            return;
        }

        const requestUser: UserEntity = yield select(getRequestUser);

        try {
            const response: ApiResponseToggleWishlist = yield call(
                () => xhr.post(strollers_api_set_wishlist_url(requestUser.id), {id}),
            );
            yield put(strollersWishlistToggleApi.success(id, response));
        } catch (error) {
             logger.error(error);
             yield put(strollersWishlistToggleApi.failure(normalizeError(error)));
        }
    }
}

export default function* root() {
    yield all([
        takeLatest(STROLLERS_WISHLIST_IDS_LOAD, fetchWishlistIds),
        takeLatest(STROLLERS_WISHLIST_ITEMS_FETCH_TRIGGER, fetchWishlistItems),
        takeEvery(STROLLERS_WISHLIST_TOGGLE_TRIGGER, toggleWishlist),
    ]);
}
